<script setup>
  import {computed, inject, onMounted, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentPasswordStrength from "@components/PasswordStrength/PasswordStrength.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";
  import {debounce} from "es-toolkit";
  import {find, map} from "es-toolkit/compat";

  defineOptions({
    name: "PagesPrivacyPasswordShow",
  });

  const privacy = inject("$privacy");

  const authenticationMode = ref("currentPassword");
  const currentErrorMessage = ref("");
  const currentPassword = ref("");
  const currentRecoveryCode = ref("");
  const passwordConfirmation = ref("");
  const passwordConfirmationErrorMessage = ref(null);
  const passwordPlaintext = ref("");
  const passwordStrength = ref(0);
  const requiresToSelectTheCurrentUser = ref(false);
  const selectedCurrentUser = ref(null);
  const step = ref("currentPassword");
  const strengthCheckerLoaded = ref(false);
  const validCurrentAuthentication = ref(false);

  const availableUsers = computed(() => {
    return map(privacy.getUsers(), function (user) {
      return {
        value: user.id,
        name: user.name ? user.name + " (" + user.email + ")" : user.email,
      };
    });
  });

  const valid = computed(() => validPasswordMinLength.value && privacy.getErrorMessage() === null);

  const validPasswordConfirmation = computed(
    () => passwordConfirmation.value !== "" && passwordConfirmationErrorMessage.value === null,
  );

  const validPasswordMinLength = computed(
    () => passwordPlaintext.value.trim().length >= privacy.getPasswordMinLength(),
  );

  const currentRecoveryCodeLength = computed(() => {
    return currentRecoveryCode.value ? currentRecoveryCode.value.split(" ").join("").trim().length : 0;
  });

  const isPasswordStrongEnough = computed(() => passwordStrength.value >= import.meta.env.VITE_PASSWORD_STRENGTH);

  watch(
    () => passwordConfirmation.value,
    () => {
      comparePasswords();
    },
  );

  watch(
    () => currentPassword.value,
    (newValue) => {
      if (newValue.trim().length > 0) verifyCurrentPassword();
    },
  );

  watch(
    () => currentRecoveryCode.value,
    (newValue) => {
      if (currentRecoveryCode.value !== null && currentRecoveryCode.value.length > 0) {
        // remove white spaces and line breaks from recovery code
        currentRecoveryCode.value = newValue.replaceAll(/\s+/g, "");

        verifyCurrentRecoveryCode();
      }
    },
  );

  watch(
    () => selectedCurrentUser,
    (id) => {
      const currentUser = find(privacy.getUsers(), {id: id});

      if (currentUser) privacy.setCurrentUser(currentUser);

      switch (authenticationMode.value) {
        case "currentPassword":
          if (currentPassword.value) verifyCurrentPassword();
          break;
        case "recoveryCode":
          if (currentRecoveryCode.value) verifyCurrentRecoveryCode();
          break;
      }
    },
  );

  onMounted(() => {
    if (privacy.getCurrentUser() === null) {
      if (privacy.getUsers().length === 1) {
        privacy.setCurrentUser(privacy.getUsers()[0]);
      } else if (privacy.getUsers().length > 1) {
        requiresToSelectTheCurrentUser.value = true;
      }
    }
  });

  const onChange = (newUser) => {
    selectedCurrentUser.value = newUser;
  };

  const switchStep = (newStep) => {
    step.value = newStep;
  };

  const switchAuthenticationMode = (newAuthMode) => {
    reset();
    authenticationMode.value = newAuthMode;
  };

  const comparePasswords = () => {
    if (validPasswordMinLength.value && passwordConfirmation.value === "") {
      passwordConfirmationErrorMessage.value = "Bitte wiederholen Sie Ihre Eingabe.";
    } else if (validPasswordMinLength.value && passwordPlaintext.value !== passwordConfirmation.value) {
      passwordConfirmationErrorMessage.value = "Passwort stimmt nicht überein.";
    } else {
      passwordConfirmationErrorMessage.value = null;
    }
  };

  const reset = () => {
    currentPassword.value = "";
    currentRecoveryCode.value = "";
    passwordPlaintext.value = "";
    passwordConfirmation.value = "";
    currentErrorMessage.value = null;
    passwordConfirmationErrorMessage.value = null;
    validCurrentAuthentication.value = false;
  };

  const checkOnLength = () => {
    if (validPasswordMinLength.value === false) {
      privacy.setErrorMessage("Ihr Passwort muss mindestens 8 Zeichen lang sein.");
    } else if (passwordPlaintext.value.length > 64) {
      privacy.setErrorMessage("Die maximal mögliche Zeichenanzahl beträgt 64 Zeichen.");
    } else {
      privacy.setErrorMessage(null);
    }
  };

  const changePassword = () => {
    if (!valid.value) {
      privacy.setErrorMessage("Ihr Passwort muss mindestens 8 Zeichen lang sein.");
    } else {
      const callback = () => {
        privacy.initCrypt();
        privacy.setEncryptLocalStorage();

        privacy.whenCryptReady(() => {
          switchStep("passwordHasBeenChanged");
          reset();
        });
      };

      const errorCallback = () => {
        passwordPlaintext.value = "";
        passwordConfirmation.value = "";
        privacy.setErrorMessage("Ihr neues Passwort konnte nicht gespeichert werden.");
      };

      privacy.hashPlaintextPassword(passwordPlaintext.value).then((hashedPassword) => {
        privacy.setHashedPassword(hashedPassword);
        let avoidStatusUpdate = true;
        privacy.patchCurrentUserEncryption(callback, errorCallback, hashedPassword, avoidStatusUpdate);
      });
    }
  };

  const verifyCurrentPassword = () => {
    if (currentPassword.value.length < privacy.getPasswordMinLength()) {
      currentErrorMessage.value = "Ihr aktuelles Passwort ist mindestens 8 Zeichen lang.";
      validCurrentAuthentication.value = false;
    } else {
      privacy
        .hashPlaintextPassword(currentPassword.value)
        .then((hashedPassword) => {
          privacy
            .verifyPasswordWithUserEncryptionChecksum(hashedPassword)
            .then((user) => {
              if (user.id !== privacy.getCurrentUser().id) {
                currentErrorMessage.value = "Ihr aktuelles Passwort ist nicht korrekt.";
                validCurrentAuthentication.value = false;
              } else {
                currentErrorMessage.value = null;
                validCurrentAuthentication.value = true;
              }
            })
            .catch(() => {
              // TODO log error on any way
              currentErrorMessage.value = "Ihr aktuelles Passwort ist nicht korrekt.";
              validCurrentAuthentication.value = false;
            });
        })
        .catch(() => {
          // TODO log error on any way
          currentErrorMessage.value = "Es ist ein Fehler aufgetreten. Bitte probieren Sie es später erneut.";
          validCurrentAuthentication.value = false;
        });
    }
  };

  const verifyCurrentRecoveryCode = debounce(function () {
    if (currentRecoveryCodeLength.value !== privacy.getRecoveryCodeLength()) {
      currentErrorMessage.value = "Der Recovery-Code muss genau 64 Zeichen lang sein.";
      validCurrentAuthentication.value = false;
    } else {
      privacy
        .verifyRecoveryCodeWithClientEncryptionChecksum(currentRecoveryCode.value.split(" ").join(""))
        .then(() => {
          currentErrorMessage.value = null;
          validCurrentAuthentication.value = true;
        })
        .catch(() => {
          currentErrorMessage.value = "Der Recovery-Code ist nicht korrekt.";
          validCurrentAuthentication.value = false;
        });
    }
  });
</script>

<template>
  <section>
    <h2 class="sr-only text-2xl font-semibold">Datenschutzpasswort</h2>

    <article class="flex flex-col items-start space-y-6 p-4 lg:flex-row lg:space-y-0 lg:space-x-6">
      <!-- article::main -->
      <div class="w-full space-y-6 lg:max-w-4xl">
        <ComponentCard
          v-if="!privacy.hasAccountEducationStatus()"
          title="Datenschutzpasswort ändern"
          class="rounded-lg border-2 border-gray-200"
          :test-id="getTestId('default')"
        >
          <div
            v-if="$page.props.client.license.is_pro && $page.props.client.license.is_connect"
            class="max-w-xl py-2"
            :data-test="getTestId('default-card-info-message-wawi-user')"
          >
            Sie haben Ihr Datenschutzpasswort in Ihrer Warenwirtschaft hinterlegt. Da Sie sich dort Ihr aktuelles
            Datenschutzpasswort anzeigen lassen können, ist die Änderung des Datenschutzpassworts derzeit nicht möglich.
          </div>

          <div v-else>
            <div class="flex flex-col gap-5">
              <template v-if="step === 'currentPassword'">
                <ComponentAlert type="warning" class="mb-0!" :test-id="getTestId('default-card-close-all-browser')">
                  <div class="flex items-center">
                    <ComponentIcon class="p4umc-primary mr-2 text-xl">warning</ComponentIcon>

                    <span>Alle weiteren Browser-Fenster schließen, in denen MediCheck geöffnet ist!</span>
                  </div>
                </ComponentAlert>

                <h5 class="text-xl">Schritt 1: Ihr aktuelles Datenschutzpasswort</h5>

                <template v-if="requiresToSelectTheCurrentUser">
                  <p>
                    In Ihrem MediCheck sind mehrere Nutzer hinterlegt. Das System konnte den aktuell eingeloggen Nutzer
                    nicht erkennen. Bitte wählen Sie aus, wer aktuell eingeloggt ist.
                  </p>

                  <ComponentSelect
                    v-model="selectedCurrentUser"
                    label="Aktueller Nutzer"
                    key-name="name"
                    return-type="object"
                    :options="availableUsers"
                    :test-id="getTestId('default-card-current-user')"
                    @change="onChange"
                  />
                </template>

                <template v-if="!requiresToSelectTheCurrentUser || selectedCurrentUser">
                  <template v-if="authenticationMode === 'currentPassword'">
                    <p>
                      Damit Sie Ihr Datenschutzpasswort ändern können, müssen Sie zuvor Ihr bisheriges
                      Datenschutzpasswort eingeben.
                    </p>

                    <form autocomplete="off" @submit.prevent>
                      <ComponentInput
                        v-model="currentPassword"
                        autocomplete="off"
                        label="Aktuelles Datenschutzpasswort"
                        :validation="currentErrorMessage"
                        type="password"
                        :test-id="getTestId('default-card-current-privacy-password')"
                      />
                    </form>

                    <p>
                      <button
                        class="cursor-pointer text-mcred hover:underline"
                        :data-test="getTestId('default-card-use-recovery-code-button')"
                        @click.prevent="switchAuthenticationMode('recoveryCode')"
                      >
                        Nutzen Sie Ihren Recovery-Code,
                      </button>
                      wenn Sie Ihr aktuelles Passwort vergessen haben.
                    </p>
                  </template>

                  <template v-else-if="authenticationMode === 'recoveryCode'">
                    <p>
                      Den 64 Zeichen langen Recovery-Code haben Sie sich beim Einrichten des Datenschutzpassworts
                      ausgedruckt oder gespeichert.
                    </p>

                    <form autocomplete="off" @submit.prevent>
                      <ComponentInput
                        v-model="currentRecoveryCode"
                        autocomplete="off"
                        label="Recovery-Code"
                        :validation="currentErrorMessage"
                        type="text"
                        :test-id="getTestId('default-card-current-recovery-code')"
                      />
                    </form>

                    <p>
                      Erinnern Sie sich wieder an
                      <button
                        class="cursor-pointer text-mcred hover:underline"
                        :data-test="getTestId('default-card-use-privacy-password-button')"
                        @click.prevent="switchAuthenticationMode('currentPassword')"
                      >
                        Ihr aktuelles Passwort?
                      </button>
                    </p>
                  </template>

                  <div class="flex justify-end">
                    <MedButton
                      variant="primary"
                      :disabled="!validCurrentAuthentication"
                      :data-test="getTestId('default-card-next-step-button')"
                      @click="switchStep('newPassword')"
                    >
                      weiter
                    </MedButton>
                  </div>
                </template>
              </template>

              <template v-else-if="step === 'newPassword'">
                <h5 class="text-xl">Schritt 2: Ihr neues Datenschutzpasswort</h5>

                <div v-if="privacy.hasCryptStatus('ENCRYPTION_UPDATE')" class="flex flex-col items-center">
                  <MedSpinner size="3xl" :data-test="getTestId('privacy-password-encryption-spinner')" />
                </div>

                <form v-else autocomplete="off" class="flex flex-col gap-4" @submit.prevent>
                  <MedSpinner
                    v-if="!strengthCheckerLoaded"
                    size="2xl"
                    :data-test="getTestId('privacy-password-strength-checker-spinner')"
                  />

                  <div v-show="strengthCheckerLoaded">
                    <ComponentInput
                      v-model="passwordPlaintext"
                      label="Neues Passwort"
                      :validation="privacy.getErrorMessage()"
                      type="password"
                      :test-id="getTestId('default-card-new-privacy-password')"
                      @blur="checkOnLength()"
                    />

                    <div class="mt-2 text-xs text-gray-500">
                      Mind. 8 Zeichen.
                      <br />
                      Mind. Passwortstärke mittel (orange)
                    </div>
                  </div>

                  <ComponentPasswordStrength
                    v-show="strengthCheckerLoaded && passwordPlaintext.length > 0"
                    :password="passwordPlaintext"
                    :test-id="getTestId('default-card')"
                    @loaded="strengthCheckerLoaded = true"
                    @score-changed="passwordStrength = $event"
                  />

                  <template v-if="isPasswordStrongEnough">
                    <ComponentInput
                      v-model="passwordConfirmation"
                      label="Wiederholung des neuen Passworts"
                      :validation="passwordConfirmationErrorMessage"
                      type="password"
                      :test-id="getTestId('default-card-new-privacy-password-confirmation')"
                      @blur="checkOnLength()"
                    />
                  </template>
                </form>

                <div class="flex justify-end">
                  <MedButton
                    variant="primary"
                    :disabled="
                      !validCurrentAuthentication ||
                      !valid ||
                      !isPasswordStrongEnough ||
                      (isPasswordStrongEnough && !validPasswordConfirmation)
                    "
                    :data-test="getTestId('default-card-new-privacy-password-save-button')"
                    @click="changePassword()"
                  >
                    weiter
                  </MedButton>
                </div>
              </template>

              <template v-else-if="step === 'passwordHasBeenChanged'">
                <div class="flex space-x-2">
                  <ComponentIcon class="text-2xl text-mcgreen">done</ComponentIcon>
                  <span>Ihr Datenschutzpasswort wurde erfolgreich geändert.</span>
                </div>

                <div class="flex justify-end">
                  <MedButton
                    variant="primary"
                    :data-test="getTestId('default-card-set-step-to-done-button')"
                    @click="switchStep('currentPassword')"
                  >
                    fertig
                  </MedButton>
                </div>
              </template>
            </div>
          </div>
        </ComponentCard>

        <ComponentCard
          v-else
          title="Datenschutzpasswort ändern"
          class="rounded-lg border-2 border-gray-200"
          :test-id="getTestId('education')"
        >
          <p>
            Sie verwenden eine Version des MediCheck, in der
            <strong>nur fiktive</strong>
            bzw. anonymisierte oder didaktische
            <strong>und zu Lehr- bzw. Ausbildungszwecken</strong>
            konzipierte oder
            <strong>modifizierte Fälle</strong>
            bearbeitet werden dürfen.
          </p>

          <p class="mt-4">Ein Datenschutzpasswort wurde daher nicht festgelegt.</p>
        </ComponentCard>
      </div>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:max-w-md lg:grid-cols-1">
        <ComponentCard class="rounded-lg border-2 border-gray-200" :test-id="getTestId('change-password')">
          Wenn Sie stattdessen Ihr Login-Passwort ändern möchten, ändern Sie dies bitte

          <a
            :href="$page.props.links.p4u + '/allgemein/benutzerkonto'"
            class="group/link text-mcred hover:no-underline"
            target="_blank"
            :data-test="getTestId('change-password-card-password-link')"
          >
            <span class="group-hover/link:underline">in Ihrem pharma4u-Benutzerkonto</span>

            <!-- TODO replace with button -->
            <ComponentIcon
              class="inline text-sm leading-tight text-gray-500 group-hover/link:text-gray-900 group-hover/link:no-underline"
              :clickable="true"
              title="Öffnet in neuem Tab"
            >
              launch
            </ComponentIcon>
          </a>

          im Abschnitt „Login-Daten“.
        </ComponentCard>
      </div>
    </article>
  </section>
</template>

<style lang="css" scoped>
  @media print {
    .hide-print {
      display: none;
    }

    .recovery {
      display: block;
      max-width: 80%;
      font-size: 1.4em;
      word-break: break-all;
    }
  }

  @media screen {
    .hide-screen {
      display: none;
    }
  }
</style>
